import { useState, useMemo } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserPremiumStatus, UserPremiumStatus } from "@/hooks/useUserPremiumStatus";
import { PremiumStatusBadge } from "@/components/admin/PremiumStatusBadge";
import { GrantPremiumDialog } from "@/components/admin/GrantPremiumDialog";
import { RevokePremiumDialog } from "@/components/admin/RevokePremiumDialog";
import { Loader2, Search, Crown, AlertCircle, Users, RefreshCw, Download } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

export default function PremiumStatus() {
  const { users, loading, error, refresh } = useUserPremiumStatus();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserPremiumStatus | null>(null);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { toast } = useToast();

  // Calculate statistics
  const stats = useMemo(() => {
    const totalUsers = users.length;
    const premiumUsers = users.filter(u => u.isPremium && !u.isAdmin).length;
    const trialActive = users.filter(u => !u.isPremium && !u.trialExpired && !u.isAdmin).length;
    const trialExpired = users.filter(u => !u.isPremium && u.trialExpired && !u.isAdmin).length;
    const adminUsers = users.filter(u => u.isAdmin).length;

    return {
      totalUsers,
      premiumUsers,
      trialActive,
      trialExpired,
      adminUsers
    };
  }, [users]);

  // Filter and categorize users
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return users.filter(u => 
      u.email.toLowerCase().includes(query) || 
      u.name.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const categorizedUsers = useMemo(() => {
    const trialActiveUsers = filteredUsers.filter(u => !u.isPremium && !u.trialExpired && !u.isAdmin);
    // Sort by oldest trial first (earliest createdAt)
    trialActiveUsers.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    
    return {
      premium: filteredUsers.filter(u => u.isPremium && !u.isAdmin),
      trialActive: trialActiveUsers,
      trialExpiring: filteredUsers.filter(u => !u.isPremium && !u.trialExpired && u.trialDaysRemaining <= 7 && !u.isAdmin),
      trialExpired: filteredUsers.filter(u => !u.isPremium && u.trialExpired && !u.isAdmin),
      admin: filteredUsers.filter(u => u.isAdmin)
    };
  }, [filteredUsers]);

  const handleGrantSuccess = () => {
    refresh();
  };

  const handleRevokeSuccess = () => {
    refresh();
  };

  const openGrantDialog = (user: UserPremiumStatus) => {
    setSelectedUser(user);
    setGrantDialogOpen(true);
  };

  const openRevokeDialog = (user: UserPremiumStatus) => {
    setSelectedUser(user);
    setRevokeDialogOpen(true);
  };

  const exportToCSV = () => {
    // Get the current filtered users based on active tab
    let usersToExport: UserPremiumStatus[] = [];
    
    switch (activeTab) {
      case "all":
        usersToExport = filteredUsers;
        break;
      case "premium":
        usersToExport = categorizedUsers.premium;
        break;
      case "trial-active":
        usersToExport = categorizedUsers.trialActive;
        break;
      case "trial-expiring":
        usersToExport = categorizedUsers.trialExpiring;
        break;
      case "trial-expired":
        usersToExport = categorizedUsers.trialExpired;
        break;
      case "admin":
        usersToExport = categorizedUsers.admin;
        break;
      default:
        usersToExport = filteredUsers;
    }

    if (usersToExport.length === 0) {
      toast({
        title: "No users to export",
        description: "The current filter has no users.",
        variant: "destructive",
      });
      return;
    }

    // Create CSV content
    const headers = ["Name", "Email", "Status", "Premium Source", "Trial Days Remaining", "Trial Expired", "Is Admin", "Created At"];
    const rows = usersToExport.map(user => [
      user.name,
      user.email,
      user.isPremium ? "Premium" : (user.trialExpired ? "Trial Expired" : "Trial Active"),
      user.premiumSource || "none",
      user.trialDaysRemaining?.toString() || "N/A",
      user.trialExpired ? "Yes" : "No",
      user.isAdmin ? "Yes" : "No",
      new Date(user.createdAt).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `users-${activeTab}-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export successful",
      description: `Exported ${usersToExport.length} users to CSV`,
    });
  };

  const renderUserCard = (user: UserPremiumStatus, showTrialInfo: boolean = false) => (
    <Card key={user.userId} className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base truncate">{user.name}</CardTitle>
            <CardDescription className="truncate">{user.email}</CardDescription>
          </div>
          <PremiumStatusBadge
            isPremium={user.isPremium}
            premiumSource={user.premiumSource}
            isAdmin={user.isAdmin}
            trialDaysRemaining={user.trialDaysRemaining}
            trialExpired={user.trialExpired}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Trial Explanation */}
        {showTrialInfo && (
          <div className="bg-muted/50 rounded-md p-3 space-y-1">
            <p className="text-sm font-medium">Trial Status</p>
            <p className="text-xs text-muted-foreground">
              Started {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
            </p>
            <p className="text-xs text-muted-foreground">
              {user.trialDaysRemaining} days remaining
            </p>
            {user.trialDaysRemaining <= 7 && (
              <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                Expires soon - enforcement will trigger
              </p>
            )}
          </div>
        )}
        
        {/* Default Info */}
        {!showTrialInfo && (
          <p className="text-xs text-muted-foreground">
            Joined {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
          </p>
        )}

        {/* Actions */}
        {!user.isAdmin && (
          <div className="flex gap-2 pt-2">
            {user.premiumSource === 'override' ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => openRevokeDialog(user)}
                className="w-full"
              >
                Ungrant Premium
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => openGrantDialog(user)}
                className="w-full"
              >
                <Crown className="h-4 w-4 mr-1" />
                Grant Premium
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-lg font-medium">Error loading users</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={refresh} className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <PageHeader
          heading="Premium Status Management"
          subheading="Monitor and manage user premium access and trials"
        />
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={refresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <p className="text-2xl font-bold">{stats.totalUsers}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Premium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-green-500" />
              <p className="text-2xl font-bold">{stats.premiumUsers}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Trial Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-500" />
              <p className="text-2xl font-bold">{stats.trialActive}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Trial Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-2xl font-bold">{stats.trialExpired}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Admins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-purple-500" />
              <p className="text-2xl font-bold">{stats.adminUsers}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* User Lists */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Users ({filteredUsers.length})</TabsTrigger>
          <TabsTrigger value="premium">Premium ({categorizedUsers.premium.length})</TabsTrigger>
          <TabsTrigger value="trial-active">Trial Active ({categorizedUsers.trialActive.length})</TabsTrigger>
          <TabsTrigger value="trial-expiring">Expiring Soon ({categorizedUsers.trialExpiring.length})</TabsTrigger>
          <TabsTrigger value="trial-expired">Expired ({categorizedUsers.trialExpired.length})</TabsTrigger>
          <TabsTrigger value="admin">Admins ({categorizedUsers.admin.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {filteredUsers.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No users found
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUsers.map(user => renderUserCard(user, false))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="premium">
          {categorizedUsers.premium.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No premium users found
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedUsers.premium.map(user => renderUserCard(user, false))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="trial-active">
          {categorizedUsers.trialActive.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No users on active trial
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedUsers.trialActive.map(user => renderUserCard(user, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="trial-expiring">
          {categorizedUsers.trialExpiring.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No trials expiring soon
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedUsers.trialExpiring.map(user => renderUserCard(user, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="trial-expired">
          {categorizedUsers.trialExpired.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No expired trials
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedUsers.trialExpired.map(user => renderUserCard(user, false))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="admin">
          {categorizedUsers.admin.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No admin users found
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorizedUsers.admin.map(user => renderUserCard(user, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {selectedUser && (
        <>
          <GrantPremiumDialog
            open={grantDialogOpen}
            onOpenChange={setGrantDialogOpen}
            userId={selectedUser.userId}
            userName={selectedUser.name}
            userEmail={selectedUser.email}
            onSuccess={handleGrantSuccess}
          />
          <RevokePremiumDialog
            open={revokeDialogOpen}
            onOpenChange={setRevokeDialogOpen}
            userId={selectedUser.userId}
            userName={selectedUser.name}
            userEmail={selectedUser.email}
            onSuccess={handleRevokeSuccess}
          />
        </>
      )}
    </div>
  );
}
